<template>
  <div v-if="showPopup" id="popup-wrapper" class="popup-wrapper">
    <div class="popup-backdrop" @click="closeAll"></div>
    <div class="popup-container">
      <div class="slides" ref="slider" @scroll="onScroll">
        <div
          v-for="(popup, index) in popups"
          :key="popup.id"
          class="slide"
          :class="{ active: index === activeIndex, inactive: index !== activeIndex }"
        >
          <img
            class="image"
            :src="popup.image"
            :alt="popup.title || 'Promotion'"
          />
        </div>
      </div>

      <!-- Single set of controls tied to activeIndex -->
      <div class="popup-action" v-if="currentPopup">
        <a class="dismiss" @click.prevent="dismiss(currentPopup.id)">
          <span class="icon"><i class="bi bi-x-lg"></i></span>
          <span class="text">No Thanks</span>
        </a>
        <a class="play-now" @click.prevent="playNow(currentPopup.id, currentPopup.route)">
          <span class="icon"><i class="bi bi-play-fill"></i></span>
          <span class="text">Play Now</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import casinoserve from '@/services/casinoserve.js'
import emitter from '@/services/eventbus.js'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const showPopup   = ref(false)
const popups      = ref([])
const slider      = ref(null)
const autoCloseT  = ref(null)
const autoSlideT  = ref(null)
const activeIndex = ref(0)
const route       = useRoute()

const currentPopup = computed(() => popups.value[activeIndex.value] || null)

function closeAll() {
  // mark only displayed popups as shown
  popups.value.slice(0, activeIndex.value + 1).forEach(p => {
    sessionStorage.setItem(`popupShown_${p.id}`, 'true')
  })
  showPopup.value = false
  clearTimers()
}

function clearTimers() {
  clearTimeout(autoCloseT.value)
  clearInterval(autoSlideT.value)
}

function dismiss(id) {
  sessionStorage.setItem(`popupShown_${id}`, 'true')
  popups.value = popups.value.filter(p => p.id !== id)
  if (!popups.value.length) closeAll()
  else activeIndex.value = Math.min(activeIndex.value, popups.value.length - 1)
}

function playNow(id, url) {
  sessionStorage.setItem(`popupShown_${id}`, 'true')
  window.location.href = url
  closeAll()
}

function scrollToIndex(index) {
  if (!slider.value) return
  const slideEl = slider.value.children[index]
  if (!slideEl) return
  const containerWidth = slider.value.clientWidth
  const slideWidth = slideEl.clientWidth
  const offsetLeft = slideEl.offsetLeft - (containerWidth - slideWidth) / 2
  slider.value.scrollTo({ left: offsetLeft, behavior: 'smooth' })
}

function startAutoSlide() {
  clearInterval(autoSlideT.value)
  autoSlideT.value = setInterval(() => {
    if (!popups.value.length) return
    activeIndex.value = (activeIndex.value + 1) % popups.value.length
    scrollToIndex(activeIndex.value)
  }, 5000)
}

function autoClose() {
  clearTimeout(autoCloseT.value)
  autoCloseT.value = setTimeout(closeAll, 35000)
}

function onScroll() {
  if (!slider.value) return
  const slidesArray = Array.from(slider.value.children)
  let closest = 0
  let minDiff = Infinity
  slidesArray.forEach((el, idx) => {
    const containerCenter = slider.value.clientWidth / 2
    const slideCenter = el.offsetLeft - slider.value.scrollLeft + el.clientWidth / 2
    const diff = Math.abs(containerCenter - slideCenter)
    if (diff < minDiff) {
      minDiff = diff
      closest = idx
    }
  })
  activeIndex.value = closest
  startAutoSlide()
}

function checkAndShow() {
  const now = dayjs()
  const today = now.format('YYYY-MM-DD')
  if (localStorage.getItem('popupDisplayedToday') === today) return

  const eligible = popups.value.filter(p => {
    const [sd] = p.start_date.split(/T| /)
    const [ed] = p.end_date.split(/T| /)
    const start = dayjs(sd, 'YYYY-MM-DD')
    const end   = dayjs(ed, 'YYYY-MM-DD')
    if (!now.isSameOrAfter(start, 'day') || !now.isSameOrBefore(end, 'day')) return false

    if (p.start_time && p.end_time) {
      const ts = dayjs(`${today}T${p.start_time}`)
      const te = dayjs(`${today}T${p.end_time}`)
      if (!now.isSameOrAfter(ts) || !now.isBefore(te)) return false
    }

    if (sessionStorage.getItem(`popupShown_${p.id}`)) return false
    if (route.path.includes('/share/')) return false
    return true
  })

  if (eligible.length) {
    popups.value = eligible
    showPopup.value = true
    localStorage.setItem('popupDisplayedToday', today)
    nextTick(() => {
      activeIndex.value = 0
      scrollToIndex(0)
      startAutoSlide()
      autoClose()
    })
  }
}

async function fetchPopups() {
  try {
    const path = `${import.meta.env.VITE_BASE_CASINOSERVE_URL}/promo/pop_up`
    const res  = await casinoserve.get(path)
    popups.value = Array.isArray(res.data)
      ? res.data.map(p => ({
        id:         p.id,
        title:      p.title,
        image:      p.image_url,
        route:      p.redirect_url,
        start_date: p.start_date,
        end_date:   p.end_date,
        start_time: p.start_time,
        end_time:   p.end_time,
      }))
      : []
  } catch (err) {
    console.error('Error fetching popups:', err)
    emitter.emit(err)
    popups.value = []
  } finally {
    checkAndShow()
  }
}

function clearPopupShownKeys() {
  const today = dayjs().format('YYYY-MM-DD')
  if (localStorage.getItem('lastClearedDate') !== today) {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (key?.startsWith('popupShown_')) sessionStorage.removeItem(key)
    }
    localStorage.removeItem('popupDisplayedToday')
    localStorage.setItem('lastClearedDate', today)
  }
}

onMounted(() => {
  clearPopupShownKeys()
  fetchPopups()
})
</script>

<style>
#popup-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.popup-backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
}
.popup-container {
  position: relative;
  width: 80%;
  max-width: 340px;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
}
.slides {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding: 0 calc((100% - 85%) / 2);
}
.slide {
  flex: 0 0 85%;
  scroll-snap-align: center;
  transform-origin: center center;
  transition: transform 0.3s, opacity 0.3s;
}
.slide.inactive {
  transform: scale(0.8);
  opacity: 0.5;
}
.slide.active {
  transform: scale(1);
  opacity: 1;
}
.slide.inactive .image {
  border-radius: 12px;
}
.image {
  width: 100%;
  display: block;
  border-radius: inherit;
}
.popup-action {
  display: flex;
  justify-content: space-around;
  padding: 10px;
}
.dismiss,
.play-now {
  flex: 0 0 48%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 12px 0;
  border-radius: 5px;
  color: white;
  text-decoration: none;
}
.dismiss { background-color: red; }
.play-now { background-color: green; }
.icon { font-size: 1.2em; margin-right: 5px; }
</style>